import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, X, Sparkles, Boxes, Network, FileJson,
  Bot, Menu, Store, Package, Activity, ChevronDown,
} from "lucide-react";
import { useT } from "@/i18n";
import Logo from "@/components/common/Logo";
import ThemeToggle from "@/components/common/ThemeToggle";
import LanguageToggle from "@/components/common/LanguageToggle";
import CookieConsent from "@/components/common/CookieConsent";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

function Section({ children, className = "", id }) {
  return (
    <section id={id} className={`mx-auto w-full max-w-6xl px-6 ${className}`}>
      {children}
    </section>
  );
}

function Overline({ children }) {
  return <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">{children}</p>;
}

function Navbar() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header className={`sticky top-0 z-50 border-b transition-colors ${scrolled ? "glass border-border/60" : "border-transparent bg-background"}`}>
      <nav className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
        <Logo />
        <div className="hidden items-center gap-8 md:flex">
          <a href="#why" className="text-sm text-muted-foreground transition-colors hover:text-foreground">{t("nav.why")}</a>
          <a href="#how" className="text-sm text-muted-foreground transition-colors hover:text-foreground">{t("nav.how")}</a>
          <a href="#different" className="text-sm text-muted-foreground transition-colors hover:text-foreground">{t("nav.different")}</a>
          <a href="#faq" className="text-sm text-muted-foreground transition-colors hover:text-foreground">{t("nav.faq")}</a>
        </div>
        <div className="flex items-center gap-2">
          <LanguageToggle />
          <ThemeToggle />
          <Link to="/login" className="hidden rounded-md px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary sm:inline-flex" data-testid="nav-login">
            {t("nav.login")}
          </Link>
          <Link to="/register" className="hidden items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-transform hover:opacity-90 active:scale-95 sm:inline-flex" data-testid="nav-get-started">
            {t("nav.getStarted")}
          </Link>
          <button className="md:hidden" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-btn" aria-label="Menu">
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </nav>
      {open && (
        <div className="border-t border-border bg-background px-6 py-4 md:hidden">
          <div className="flex flex-col gap-3">
            <a href="#why" onClick={() => setOpen(false)} className="text-sm text-muted-foreground">{t("nav.why")}</a>
            <a href="#how" onClick={() => setOpen(false)} className="text-sm text-muted-foreground">{t("nav.how")}</a>
            <a href="#different" onClick={() => setOpen(false)} className="text-sm text-muted-foreground">{t("nav.different")}</a>
            <a href="#faq" onClick={() => setOpen(false)} className="text-sm text-muted-foreground">{t("nav.faq")}</a>
            <Link to="/login" className="text-sm font-medium">{t("nav.login")}</Link>
            <Link to="/register" className="rounded-md bg-primary px-4 py-2 text-center text-sm font-medium text-primary-foreground">{t("nav.getStarted")}</Link>
          </div>
        </div>
      )}
    </header>
  );
}

function DashboardMockup() {
  const t = useT();
  return (
    <div className="relative w-full rounded-xl border border-border bg-card p-4 shadow-2xl shadow-black/10">
      <div className="mb-4 flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-destructive/60" />
        <span className="h-2.5 w-2.5 rounded-full bg-warning/60" />
        <span className="h-2.5 w-2.5 rounded-full bg-success/60" />
        <span className="ml-3 font-mono text-xs text-muted-foreground">geolyshop.com/dashboard</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-border p-4">
          <div className="flex items-center gap-2 text-muted-foreground"><Store className="h-4 w-4" /><span className="text-xs">{t("landing.mockStoreStatus")}</span></div>
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
            <span className="h-1.5 w-1.5 rounded-full bg-success" />{t("landing.mockPublished")}
          </div>
        </div>
        <div className="row-span-2 flex flex-col items-center justify-center rounded-lg border border-border bg-primary p-4 text-primary-foreground">
          <span className="text-xs text-primary-foreground/60">{t("landing.mockAiScore")}</span>
          <span className="font-mono text-4xl font-bold text-brand">87</span>
          <span className="text-[10px] uppercase tracking-widest text-primary-foreground/50">/100</span>
        </div>
        <div className="rounded-lg border border-border p-4">
          <div className="flex items-center gap-2 text-muted-foreground"><Package className="h-4 w-4" /><span className="text-xs">{t("landing.mockProducts")}</span></div>
          <span className="mt-1 block font-mono text-2xl font-bold">24</span>
        </div>
      </div>
      <div className="mt-3 rounded-lg border border-border p-3">
        <div className="mb-2 flex items-center gap-2 text-muted-foreground"><Activity className="h-4 w-4" /><span className="text-xs">{t("landing.mockLastSync")}: {t("landing.mockNow")}</span></div>
        <div className="space-y-1.5 font-mono text-[10px] text-muted-foreground">
          <div className="flex justify-between"><span>@type: Product</span><span className="text-brand">✓ schema</span></div>
          <div className="flex justify-between"><span>knowledgeGraph.nodes: 8</span><span className="text-brand">✓ graph</span></div>
          <div className="flex justify-between"><span>faq.generated: 5</span><span className="text-brand">✓ faq</span></div>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const t = useT();

  const steps = [
    { icon: Store, title: t("landing.howStep1Title"), body: t("landing.howStep1Body") },
    { icon: Package, title: t("landing.howStep2Title"), body: t("landing.howStep2Body") },
    { icon: Sparkles, title: t("landing.howStep3Title"), body: t("landing.howStep3Body") },
    { icon: Bot, title: t("landing.howStep4Title"), body: t("landing.howStep4Body") },
  ];
  const whyPoints = [
    { icon: Bot, title: t("landing.whyPoint1Title"), body: t("landing.whyPoint1Body") },
    { icon: Network, title: t("landing.whyPoint2Title"), body: t("landing.whyPoint2Body") },
    { icon: Sparkles, title: t("landing.whyPoint3Title"), body: t("landing.whyPoint3Body") },
  ];
  const diffRows = [
    [t("landing.diffRow1a"), t("landing.diffRow1b")],
    [t("landing.diffRow2a"), t("landing.diffRow2b")],
    [t("landing.diffRow3a"), t("landing.diffRow3b")],
    [t("landing.diffRow4a"), t("landing.diffRow4b")],
    [t("landing.diffRow5a"), t("landing.diffRow5b")],
    [t("landing.diffRow6a"), t("landing.diffRow6b")],
  ];
  const faqs = [
    [t("landing.faq1Q"), t("landing.faq1A")],
    [t("landing.faq2Q"), t("landing.faq2A")],
    [t("landing.faq3Q"), t("landing.faq3A")],
    [t("landing.faq4Q"), t("landing.faq4A")],
    [t("landing.faq5Q"), t("landing.faq5A")],
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* HERO */}
      <Section className="relative pt-20 pb-24 md:pt-28">
        <div className="dotgrid pointer-events-none absolute inset-0 -z-10 opacity-40 [mask-image:radial-gradient(ellipse_at_top,black,transparent_70%)]" />
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <motion.div initial="hidden" animate="show" variants={fade} custom={0}>
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3 py-1 text-xs font-medium">
                <span className="h-1.5 w-1.5 rounded-full bg-brand pulse-dot" />{t("landing.heroBadge")}
              </span>
            </motion.div>
            <motion.h1 initial="hidden" animate="show" variants={fade} custom={1}
              className="mt-6 font-display text-5xl font-black leading-[1.05] tracking-tighter sm:text-6xl lg:text-7xl">
              {t("landing.heroTitle")}
            </motion.h1>
            <motion.p initial="hidden" animate="show" variants={fade} custom={2}
              className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">
              {t("landing.heroSubtitle")}
            </motion.p>
            <motion.div initial="hidden" animate="show" variants={fade} custom={3} className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta"
                className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 font-medium text-primary-foreground transition-transform hover:opacity-90 active:scale-95">
                {t("landing.heroCta")}<ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#how" className="inline-flex items-center gap-2 rounded-md border border-border px-6 py-3 font-medium transition-colors hover:bg-secondary">
                {t("landing.heroSecondary")}
              </a>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}>
            <DashboardMockup />
          </motion.div>
        </div>
      </Section>

      {/* WHY */}
      <Section id="why" className="border-t border-border py-24">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
          <Overline>{t("landing.whyOverline")}</Overline>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">{t("landing.whyTitle")}</h2>
          <div className="mt-6 max-w-2xl space-y-4 text-lg leading-relaxed text-muted-foreground">
            <p>{t("landing.whyBody1")}</p>
            <p>{t("landing.whyBody2")}</p>
          </div>
        </motion.div>
        <div className="mt-12 grid gap-4 md:grid-cols-3">
          {whyPoints.map((p, i) => (
            <motion.div key={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i}
              className="group rounded-lg border border-border p-6 transition-transform hover:-translate-y-1">
              <p.icon className="h-6 w-6 text-brand" />
              <h3 className="mt-4 font-display text-lg font-bold">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* MANIFESTO */}
      <Section className="border-t border-border py-24">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
          className="relative overflow-hidden rounded-2xl border border-border bg-secondary/30 p-10 sm:p-16">
          <div className="dotgrid pointer-events-none absolute inset-0 opacity-40 [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]" />
          <div className="relative z-10 max-w-3xl">
            <h2 className="font-display text-3xl font-extrabold leading-tight tracking-tighter sm:text-4xl">
              {t("landing.manifestoTitle")}<br />
              <span className="text-brand">{t("landing.manifestoHighlight")}</span>
            </h2>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">{t("landing.manifestoBody")}</p>
          </div>
        </motion.div>
      </Section>

      {/* HOW */}
      <Section id="how" className="border-t border-border py-24">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
          <Overline>{t("landing.howOverline")}</Overline>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">{t("landing.howTitle")}</h2>
        </motion.div>
        <div className="mt-12 grid gap-px overflow-hidden rounded-lg border border-border bg-border md:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <motion.div key={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i}
              className="bg-card p-6">
              <div className="flex items-center justify-between">
                <span className="font-mono text-3xl font-bold text-border">{String(i + 1).padStart(2, "0")}</span>
                <s.icon className="h-5 w-5 text-brand" />
              </div>
              <h3 className="mt-6 font-display text-lg font-bold">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* DIFFERENT */}
      <Section id="different" className="border-t border-border py-24">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
          <Overline>{t("landing.diffOverline")}</Overline>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">{t("landing.diffTitle")}</h2>
        </motion.div>
        <div className="mt-12 overflow-hidden rounded-lg border border-border">
          <div className="grid grid-cols-2 border-b border-border">
            <div className="p-4 text-sm font-medium text-muted-foreground">{t("landing.diffColTraditional")}</div>
            <div className="border-l border-border bg-secondary/50 p-4 font-display text-sm font-bold">{t("landing.diffColAi")}</div>
          </div>
          {diffRows.map(([a, b], i) => (
            <div key={i} className={`grid grid-cols-2 ${i % 2 ? "bg-background" : "bg-secondary/20"}`}>
              <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
                <X className="h-4 w-4 shrink-0 text-muted-foreground/60" />{a}
              </div>
              <div className="flex items-center gap-2 border-l border-border bg-secondary/40 p-4 text-sm font-medium">
                <Check className="h-4 w-4 shrink-0 text-brand" />{b}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq" className="border-t border-border py-24">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}>
          <Overline>{t("landing.faqOverline")}</Overline>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">{t("landing.faqTitle")}</h2>
        </motion.div>
        <Accordion type="single" collapsible className="mt-8 max-w-3xl">
          {faqs.map(([q, a], i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-display text-lg font-semibold">{q}</AccordionTrigger>
              <AccordionContent className="text-base leading-relaxed text-muted-foreground">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      {/* CTA */}
      <Section className="pb-24">
        <div className="relative overflow-hidden rounded-2xl bg-primary px-8 py-16 text-center text-primary-foreground">
          <div className="dotgrid absolute inset-0 opacity-[0.07]" />
          <div className="relative z-10">
            <h2 className="mx-auto max-w-2xl font-display text-3xl font-extrabold tracking-tighter sm:text-5xl">{t("landing.ctaTitle")}</h2>
            <p className="mx-auto mt-4 max-w-xl text-primary-foreground/70">{t("landing.ctaSubtitle")}</p>
            <Link to="/register" data-testid="cta-final"
              className="mt-8 inline-flex items-center gap-2 rounded-md bg-brand px-6 py-3 font-medium text-brand-foreground transition-transform hover:opacity-90 active:scale-95">
              {t("landing.ctaButton")}<ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="border-t border-border">
        <Section className="flex flex-col items-center justify-between gap-4 py-8 sm:flex-row">
          <Logo />
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
            <Link to="/privacy" data-testid="footer-privacy" className="transition-colors hover:text-foreground">{t("legal.privacyTitle")}</Link>
            <Link to="/terms" data-testid="footer-terms" className="transition-colors hover:text-foreground">{t("legal.termsTitle")}</Link>
            <Link to="/cookie" data-testid="footer-cookie" className="transition-colors hover:text-foreground">{t("legal.cookieTitle")}</Link>
          </div>
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} GeolyShop. {t("landing.footerRights")}</p>
        </Section>
      </footer>

      <CookieConsent />
    </div>
  );
}
